import React, { useEffect, useState } from 'react';
import * as Yup from "yup";
import Breadcrumbs from '../../components/BreadCrumbs';
import { toast } from 'react-toastify';
import { useLocation, useNavigate } from 'react-router-dom';
import { Form, Formik } from 'formik';
import Button from '../../components/buttons/Button';
import InputField from '../../components/inputFields/InputField';
import FormikDropdown from '../../components/inputFields/FormikDropDown';
import FilePickerField from '../../components/inputFields/FilePickerField';
import { patchCommunity, postCommunity } from '../../services/community';
import { isProd } from '../../constants/apis';
import { getUserId } from '../../helpers/local_storage_helpers';

function CommunityForm() {
    const location = useLocation();
    const navigate = useNavigate();
    const [rowData, setRowData] = useState(null);
    const [file, setFile] = useState(null);
    const [description, setDescription] = useState("");
    const [inputType, setInputType] = useState(rowData?.youtube_link ? "youtube" : "file"); // Add this state


    const handleDiscardChanges = (resetForm) => {
        resetForm();
        navigate(-1);
    };

    const handleimageSelect = (file) => {
        setFile(file);
    };

    const handleFormSubmit = async (values, { setSubmitting }) => {
        const formData = new FormData();
        if (rowData !== null) {
            if (file && typeof file === "object" && values.youtube_link !== "") {
                toast.error("Choose either file or provide youtube link");
                return;
            }
        }
        if (file && typeof file === "object") {
            formData.append("file", file);
        } else if (values.youtube_link !== "") {
            formData.append("youtube_link", values.youtube_link ?? "");
        } else if (rowData === null) {
            toast.error("Choose either file or provide youtube link");
            return;
        }
        formData.append('description', description);
        formData.append("type", values.type);
        formData.append("source", values.source);
        formData.append("sub_type", values.sub_type);
        formData.append("title", values.title);
        if (values.link !== "") {
            formData.append("link", values.link ?? "");
        }
        formData.append("slug", values.slug);
        formData.append("location", values.location);
        formData.append("is_approved", values.is_approved);
        formData.append("is_active", values.is_active);
        formData.append("user", isProd ? "5" : "");
        formData.append("approved_by", getUserId());

        try {
            const response = rowData ? await patchCommunity({ data: formData, id: rowData.id }) : await postCommunity(formData);

            if (response?.status === 200 || response.status === 201) {
                toast.success(rowData ? "Post updated successfully" : "Community added successfully!");
                navigate(-1);
            } else {
                let errors = "Opps! Something went wrong, please try again.";
                toast.error(errors);
            }
        } catch (error) {
            let errors =
                error?.response?.data?.error ||
                "Opps! Something went wrong, please try again.";
            toast.error(errors);
        }
        setSubmitting(false);
    };

    useEffect(() => {
        if (location.state) {
            setRowData(location.state);
            if (location.state.description) {
                setDescription(location.state.description);
            }
            if (location.state.file) {
                setFile(location.state.file);
                setInputType('file');
            } else if (location.state.youtube_link) {
                setInputType('youtube');
            }
        }
    }, [location]);

    return (<>
        <Breadcrumbs items={[
            { name: 'Home', link: '/dashboard' },
            { name: 'Community', link: '/marketing/community' },
            { name: rowData ? 'Update Community' : 'Add Community' },
        ]} />
        <div className="mt-8">
            <Formik
                initialValues={rowData || {
                    type: "General",
                    source: "",
                    sub_type: "",
                    title: "",
                    link: "",
                    youtube_link: "",
                    slug: "",
                    location: "",
                    is_approved: true,
                    is_active: true,
                }}
                validationSchema={Yup.object({
                    type: Yup.string().required("Type is required"),
                    title: Yup.string().required("Title is required"),
                })}
                enableReinitialize
                onSubmit={handleFormSubmit}
            >
                {({ isSubmitting, resetForm, values, setValues }) => (
                    <Form>
                        <div className="border border-gray-300 mt-5 p-4 rounded-lg bg-gray-100 flex flex-col md:flex-row gap-4">
                            <div className="flex flex-col w-full">
                                <div className="grid gap-2 grid-cols-1 md:grid-cols-3 mb-3">
                                    <FormikDropdown
                                        label="Post Type"
                                        name="type"
                                        placeholder="Post Type"
                                        options={[
                                            { label: "General", value: "General" },
                                            { label: "Updates", value: "Updates" },
                                            { label: "News", value: "News" },
                                            { label: "Scheme", value: "Scheme" },
                                            { label: "New Products", value: "New Products" },
                                            { label: "Govt. Scheme", value: "Govt. Scheme" },
                                            { label: "Products", value: "Products" },
                                            { label: "Harvest", value: "Harvest" },
                                            { label: "Knowledge", value: "Knowledge" },
                                        ]}
                                    />
                                    {
                                        values.type === "News" ||
                                            values.type === "Scheme" ?
                                            <InputField
                                                label="Source"
                                                name="source"
                                                type="text"
                                                placeholder="ABP News"
                                                required
                                            /> : <></>
                                    }
                                    {
                                        values.type === "News" ||
                                            values.type === "Scheme" ?
                                            <InputField
                                                label="Sub Type"
                                                name="sub_type"
                                                type="text"
                                                placeholder="Sub Type"
                                            /> : <></>
                                    }
                                    <InputField
                                        label="Title"
                                        name="title"
                                        type="text"
                                        placeholder="Post title"
                                        required
                                    />
                                    <InputField
                                        label="Article Link"
                                        name="link"
                                        type="url"
                                        placeholder="Article Link"
                                    />
                                </div>
                                <div className="grid gap-2 grid-cols-1 md:grid-cols-2 mb-3">
                                    <div className="flex flex-col space-y-2">
                                        <label className="font-medium text-sm">Content Type</label>
                                        <div className="flex items-center space-x-4">
                                            <div 
                                                className={`px-4 py-2 rounded-md cursor-pointer ${inputType === 'file' ? 'bg-primary text-white' : 'bg-gray-200'}`}
                                                onClick={() => setInputType('file')}
                                            >
                                                File Upload
                                            </div>
                                            <div 
                                                className={`px-4 py-2 rounded-md cursor-pointer ${inputType === 'youtube' ? 'bg-primary text-white' : 'bg-gray-200'}`}
                                                onClick={() => setInputType('youtube')}
                                            >
                                                YouTube Link
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="grid gap-2 grid-cols-1 md:grid-cols-2 mb-3">
                                    {inputType === 'file' && (
                                        <FilePickerField
                                            preloadedFile={file}
                                            label='File'
                                            placeHolder='Select File'
                                            onFileSelect={handleimageSelect}
                                        />
                                    )}
                                    {inputType === 'youtube' && (
                                        <InputField
                                            label="Youtube Link"
                                            name="youtube_link"
                                            type="url"
                                            placeholder="Youtube Link"
                                        />
                                    )}
                                    <InputField
                                        label="Slug"
                                        name="slug"
                                        type="text"
                                        placeholder="Slug (For title language other than english please provide)*"
                                    />
                                    <InputField
                                        label="Location"
                                        name="location"
                                        type="text"
                                        placeholder="Indore, Madhya Pradesh"
                                    />
                                </div>
                                <textarea className="flex-1 p-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-green-400" id="" onChange={(e) => setDescription(e.target.value)} value={description} placeholder="Description" ></textarea>
                                <div className="grid gap-2 grid-cols-1 md:grid-cols-2 mt-3">
                                    <InputField
                                        label="Show post on App"
                                        name="is_active"
                                        isCheckbox={true}
                                    />
                                    <InputField
                                        label="Approved"
                                        name="is_approved"
                                        isToggle={true}
                                    />
                                </div>
                                <div className="py-5 flex justify-end gap-6">
                                    <div>
                                        <Button
                                            type="button"
                                            className="text-primary bg-white hover:bg-white-dark font-sm rounded-lg text-lg w-full px-8 py-1.5 text-center border border-primary"
                                            onClick={() => handleDiscardChanges(resetForm)}
                                            disabled={isSubmitting}
                                        >
                                            Discard Changes
                                        </Button>
                                    </div>
                                    <div>
                                        <Button
                                            type="submit"
                                            className="text-white bg-primary hover:bg-primary-dark font-sm rounded-lg text-lg w-full px-8 py-1.5 text-center"
                                            disabled={isSubmitting}
                                        >
                                            {isSubmitting ? "Saving..." : "Save"}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Form>
                )}
            </Formik>
        </div>
    </>);
}

export default CommunityForm;